import { useState, useMemo } from "react";
import { icons } from "lucide-react";
import { Input } from "components/ui/input_v3";
import { ScrollArea } from "components/ui/scroll-area_v2";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover_v4";
import { Button } from "components/ui/button_v4";
import { Search } from "lucide-react";
import { cn } from "lib/utils";
import { useThemeStore } from "context/useThemeStore";

const iconEntries = Object.entries(icons).filter(
  ([name]) =>
    name !== "createLucideIcon" &&
    name !== "defaultAttributes" &&
    name !== "icons",
);

const IconPicker = ({ value, onChange }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const { theme } = useThemeStore();

  const filtered = useMemo(() => {
    if (!search) return iconEntries.slice(0, 200);
    const q = search.toLowerCase();
    return iconEntries
      .filter(([name]) => name.toLowerCase().includes(q))
      .slice(0, 200);
  }, [search]);

  const SelectedIcon = value ? icons[value] : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start gap-2 h-10">
          {SelectedIcon ? (
            <>
              <SelectedIcon className="h-4 w-4 text-primary" />
              <span className="text-sm text-foreground">{value}</span>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">
              Select an icon...
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent theme={theme} className="w-80 p-0" align="start">
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
        <ScrollArea className="h-64">
          <div className="grid grid-cols-6 gap-1 p-2">
            {filtered.map(([name, Icon]) => (
              <button
                key={name}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                  setSearch("");
                }}
                title={name}
                className={cn(
                  "flex items-center justify-center rounded-md p-2.5 transition-colors hover:bg-accent",
                  value === name && "bg-primary/10 ring-1 ring-primary",
                )}
              >
                <Icon className="h-5 w-5 text-foreground" />
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-6 text-center py-8 text-sm text-muted-foreground">
                No icons found
              </p>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default IconPicker;
